import React, {useMemo} from 'react';
import {VipConfigVO} from './vip.service';
import {Trans, useTranslation} from 'react-i18next';
import {cn, toPriceStr} from '@/utils';
import styles from './vip.module.scss';

const VipPrizeDetailItem = ({
  no,
  title,
  children,
}: {
  no: string;
  title: string;
  children?: React.ReactNode;
}) => {
  return (
    <div className="flex flex-col gap-2">
      <div
        className={cn(
          'flex flex-row items-center px-3 h-7 rounded-full text-t3',
          styles['vip-level-detail-title-bg'],
        )}>
        {no}.{title}
      </div>
      <div className="flex flex-col">{children}</div>
    </div>
  );
};

type RewardType = 'spin' | 'scratch' | 'money';

const VipLevelRewardItem = ({
  count,
  rewardType,
}: {
  count: number;
  rewardType: RewardType;
}) => {
  const icon = useMemo(() => {
    const isGray = count <= 0;
    const srcMap = {
      spin: isGray
        ? require('@assets/icons/vip/v2/spin-gray.webp')
        : require('@assets/icons/vip/v2/spin.webp'),
      scratch: isGray
        ? require('@assets/icons/vip/v2/scratch-gray.webp')
        : require('@assets/icons/vip/v2/scratch.webp'),
      money: isGray
        ? require('@assets/icons/vip/v2/money-gray.webp')
        : require('@assets/icons/vip/v2/money.webp'),
    };
    return srcMap[rewardType];
  }, [rewardType, count]);
  return (
    <div className="flex flex-row items-center gap-1">
      <div
        className="size-6 bg-no-repeat bg-full"
        style={{
          backgroundImage: `url(${icon})`,
        }}
      />
      <span className="text-[#FF9200] text-xs">
        {rewardType === 'money'
          ? `x ${toPriceStr(count, {fixed: 0})}`
          : `x${count}`}
      </span>
    </div>
  );
};

const VipPrizeDetail = ({vip}: {vip: VipConfigVO}) => {
  const {t} = useTranslation();
  return (
    <div className="flex flex-col bg-white rounded-lg gap-3 p-2.5 flex-1 overflow-y-auto mx-3 mb-3">
      <VipPrizeDetailItem no="01" title={t('vip.tip.levelDetail.t01')}>
        <p className="text-t3 text-[0.625rem]">
          <Trans
            i18nKey="vip.tip.levelDetail.p1"
            values={{level: `VIP${vip.level || 0}`}}
            components={[<span className="text-[#FF9200]" />]}
          />
        </p>
        <div className="grid grid-cols-3">
          <VipLevelRewardItem
            count={vip.leveBonusAmount || 0}
            rewardType="money"
          />
          <VipLevelRewardItem
            count={vip.scratchCount || 0}
            rewardType="scratch"
          />
          <VipLevelRewardItem count={vip.spinCount || 0} rewardType="spin" />
        </div>
      </VipPrizeDetailItem>
      <VipPrizeDetailItem no="02" title={t('vip.tip.levelDetail.t02')}>
        <p className="text-t3 text-[0.625rem]">
          <Trans
            i18nKey="vip.tip.levelDetail.p2"
            values={{
              level: `VIP${vip.level || 0}`,
              money: toPriceStr(vip.weekBaseBonusAmount || 0, {fixed: 0}),
            }}
            components={[<span className="text-[#FF9200]" />]}
          />
        </p>
      </VipPrizeDetailItem>
      <VipPrizeDetailItem no="03" title={t('vip.tip.levelDetail.t03')}>
        <p className="text-t3 text-[0.625rem]">
          <Trans
            i18nKey="vip.tip.levelDetail.p3"
            values={{
              level: `VIP${vip.level || 0}`,
              money: toPriceStr(vip.singleWithdrawalLimitAmount || 0, {
                fixed: 0,
              }),
            }}
            components={[<span className="text-[#FF9200]" />]}
          />
        </p>
      </VipPrizeDetailItem>
      <VipPrizeDetailItem no="04" title={t('vip.tip.levelDetail.t04')}>
        <p className="text-t3 text-[0.625rem]">
          <Trans
            i18nKey="vip.tip.levelDetail.p4"
            values={{
              level: `VIP${vip.level || 0}`,
              count: vip.dailyWithdrawalLimitCount || 0,
            }}
            components={[<span className="text-[#FF9200]" />]}
          />
        </p>
      </VipPrizeDetailItem>
      <VipPrizeDetailItem no="05" title={t('vip.tip.levelDetail.t05')}>
        <p className="text-t3 text-[0.625rem]">
          <Trans
            i18nKey="vip.tip.levelDetail.p5"
            values={{
              level: `VIP${vip.level || 0}`,
              money: `${+(
                (vip.singleWithdrawalFeeCoefficient || 0) * 100
              ).toFixed(2)}%`,
            }}
            components={[<span className="text-[#FF9200]" />]}
          />
        </p>
      </VipPrizeDetailItem>
    </div>
  );
};

export default VipPrizeDetail;
